import React, { ChangeEvent, useEffect, useState } from 'react'

import {
  Button,
  Col,
  Grid,
  Row,
  SimpleCollapse,
  Span,
  Select,
  CurrencyInput,
  InputMask
} from 'components'
import { LoadingIcon } from 'components/Icons'
import { useModal } from 'hooks'
import { ILoading } from 'hooks/useStatePage'
import { RequestCreateItemAnalise } from 'services/NGT/AnalyzeCRE'
import { RegisterPendingServices } from 'services/NGT/RegisterPendingItems'
import { COLORS, FONTS, SPACING, TYPOGRAPHY } from 'utils'
import { generateAleatoryID } from 'utils/functions'
import { TipoPendencia } from 'utils/types'

import { RedLine } from '../AddAnalysisCre.styles'
import { TableAnalysisCre } from '../TableAnalysisCre/TableAnalysisCre'

interface IAnaliseCollapse {
  listAnalises: RequestCreateItemAnalise[] | []
  resetPage: () => void
  handleAddAnalise: (_data) => void
  handleSalvarAnaliseCre: () => void
  disabledFunctions?: boolean
  handleLiberado: (_data) => void
  handleQtdPendencias: (_data) => void
  handleDescricaoAnexo: (_data) => void
  handleObservacaoMovDocItem: (_data) => void
  novaAnalise?: boolean
  fornecedorBloqueado: boolean
  handleRedirecionarBloqueio: () => void
  handleDeletePendency?: (_data: number | String, isIdItemCre: Boolean) => void
  isChecklist?: boolean
}

interface IAnaliseForm {
  txDescricaoAnexo: string
  tipoPendenciaId: string
  vlCalcRisco: string
  showCalcRisco: string
}

const DEFAULT_FORM = {
  showCalcRisco: '',
  tipoPendenciaId: '',
  txDescricaoAnexo: '',
  vlCalcRisco: ''
}

const AnaliseCollapse: React.FC<IAnaliseCollapse> = ({
  fornecedorBloqueado,
  handleRedirecionarBloqueio,
  disabledFunctions,
  resetPage,
  handleAddAnalise,
  handleSalvarAnaliseCre,
  listAnalises,
  novaAnalise,
  handleLiberado,
  handleQtdPendencias,
  handleDescricaoAnexo,
  handleObservacaoMovDocItem,
  handleDeletePendency,
  isChecklist,
}) => {
  const [analiseRequest, setAnaliseRequest] = useState<IAnaliseForm>(DEFAULT_FORM)
  const [loadingState, setLoadingState] = useState<ILoading>('idle')
  const [listTipoDocPendencia, setListTipoDocPendencia] = useState<TipoPendencia[] | undefined>()

  const { showModal } = useModal()
  const [tipoPendenciaErro, setTipoPendenciaErro] = useState(false)
  const [observacaoErro, setObservacaoErro] = useState('')

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'showCalcRisco') {
      setAnaliseRequest((old) => ({
        ...old,
        [e.target.name]: e.target.value,
        vlCalcRisco: e.target.value
      }))
      return
    }
    setAnaliseRequest((old) => ({
      ...old,
      [e.target.name]: e.target.value
    }))
    setTipoPendenciaErro(false)
    setObservacaoErro(null)
  }

  const getTipoDoc = async () => {
    setLoadingState('loading')

     

    try {
      const response = await RegisterPendingServices().getAll({ isTelaCRE: true });
      if (response.status !== 200) {
        throw new Error('Não foi possivel obter tipos de documento')
      }
      response?.data && setListTipoDocPendencia(response?.data)
      setLoadingState('idle') 
    } catch (error: any) {
      setLoadingState('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.message ||
          error.mensagem ||
          'Não foi possivel obter tipos de documento',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleClearForm = () => {
    setAnaliseRequest(DEFAULT_FORM)
    setTipoPendenciaErro(false)
    setObservacaoErro(null)
  }

  const handleIncludeButton = () => {
    if (!analiseRequest.tipoPendenciaId) {
      setTipoPendenciaErro(true)
    }

    if (!analiseRequest.txDescricaoAnexo) {
      setObservacaoErro('Favor informar uma observação')
    }

    if (analiseRequest.tipoPendenciaId && analiseRequest.txDescricaoAnexo) {
      handleAddAnalise({
        ...analiseRequest,
        cdStatus: 'Pendente',
        id: generateAleatoryID(10),
        qtPendencias: 0
      })
      setAnaliseRequest(DEFAULT_FORM)
    }
  }

  useEffect(() => {
    getTipoDoc()
  }, [])

  if (loadingState === 'loading') {
    return (
      <LoadingIcon
        marginLeft="auto"
        marginRight="auto"
        marginTop={SPACING.spacing20}
        marginBottom={SPACING.spacing20}
      />
    )
  }

  return (
    <SimpleCollapse title="ANÁLISE">
      <>
        {!disabledFunctions && (
          <Grid
            gridCols="1.5fr 1fr 3fr 2.5fr"
            gridGap={`0 ${SPACING.spacing20}`}
            margin={`${SPACING.spacing10} 0 ${SPACING.spacing5} 0`}
          >
            <Select
              value={analiseRequest.tipoPendenciaId}
              onChange={handleOnChange}
              formValue="tipoPendenciaId"
              label="Tipo Documento"
              valueMenu="idTipoPendencia"
              labelMenu="txDescricao"
              listOptions={listTipoDocPendencia}
              required
              customError={tipoPendenciaErro}
              disabled={!novaAnalise}
            />
            <CurrencyInput
              name="showCalcRisco"
              formValue="showCalcRisco"
              placeholder="R$"
              label="Valor de Risco"
              value={analiseRequest.showCalcRisco}
              onChange={handleOnChange}
              disabled={!novaAnalise}
            />
            <Col>
              <InputMask
                onChange={handleOnChange}
                label="Observação"
                placeholder="Observação"
                formValue="txDescricaoAnexo"
                value={analiseRequest.txDescricaoAnexo}
                maxLength={150}
                required
                customError={observacaoErro}
                disabled={!novaAnalise}
              />
              <Span
                textAlign="end"
                text={String(analiseRequest.txDescricaoAnexo?.length) + '/150'}
              />
            </Col>
          </Grid>
        )}
        <Row justifyContent="space-between" marginBottom={SPACING.spacing20}>
          {fornecedorBloqueado ? (
            <Col>
              <Span
                text="Bloqueado!"
                fontSize={TYPOGRAPHY.subtitleSize20}
                fontWeight={FONTS.bold}
                marginTop={SPACING.spacing30}
                marginBottom={SPACING.spacing5}
                color={COLORS.error}
                cursor={'pointer'}
                onClick={() => handleRedirecionarBloqueio()}
              />
              <RedLine />
            </Col>
          ) : (
            <div />
          )}
          {!disabledFunctions && (
            <Row
              alignItems="center"
              justifyContent="flex-end"
              marginTop={SPACING.spacing20}
            >
              <Button
                text="Limpar"
                variantButton="text"
                onClick={() => handleClearForm()}
                disabled={!novaAnalise}
              />

              <Button
                text="Incluir"
                onClick={() => handleIncludeButton()}
                marginLeft={SPACING.spacing20}
                disabled={!novaAnalise}
              />
            </Row>
          )}
        </Row>
      </>
      <TableAnalysisCre
        tableData={listAnalises}
        listTipoDoc={listTipoDocPendencia}
        handleLiberado={handleLiberado}
        handleQtdPendencias={handleQtdPendencias}
        handleDescricaoAnexo={handleDescricaoAnexo}
        handleObservacaoMovDocItem={handleObservacaoMovDocItem}
        novaAnalise={novaAnalise}
        handleDeletePendency={handleDeletePendency}
        isChecklist={isChecklist}
      />

      <Row justifyContent="flex-end" marginTop={SPACING.spacing20}>
        <Button text="Cancelar" variantButton="text" onClick={resetPage} />
        {!disabledFunctions && (
          <Button
            text="Salvar"
            onClick={handleSalvarAnaliseCre}
            marginLeft={SPACING.spacing20}
            disabled={!novaAnalise}
          />
        )}
      </Row>
    </SimpleCollapse>
  )
}

export default AnaliseCollapse
